import React, { useState, useEffect } from 'react';
import { X, Calendar, Users, MapPin, Euro, TrendingDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookingModalNew = ({ deal, open, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('full'); // 'full' or 'counter'
  const [formData, setFormData] = useState({
    check_in: '',
    check_out: '',
    guests: deal?.guests || 2,
    offered_price: deal?.price_per_night || 0,
    notes: ''
  });

  useEffect(() => {
    if (deal && open) {
      setFormData({
        check_in: '',
        check_out: '',
        guests: deal?.guests || 2,
        offered_price: deal?.price_per_night || 0,
        notes: ''
      });
      setMode('full');
    }
  }, [deal, open]);

  const calculateNights = () => {
    if (!formData.check_in || !formData.check_out) return 0;
    const checkIn = new Date(formData.check_in);
    const checkOut = new Date(formData.check_out);
    const nights = Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24));
    return nights > 0 ? nights : 0;
  };

  const calculateTotal = () => {
    const nights = calculateNights();
    const pricePerNight = mode === 'counter' ? formData.offered_price : deal?.price_per_night || 0;
    return nights * pricePerNight;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      
      // Validate dates
      if (!formData.check_in || !formData.check_out) {
        toast.error('Seleziona le date di check-in e check-out');
        setLoading(false);
        return;
      }

      const nights = calculateNights();
      if (nights <= 0) {
        toast.error('Il check-out deve essere dopo il check-in');
        setLoading(false);
        return;
      }

      if (mode === 'counter') {
        // Validate offered price
        if (formData.offered_price >= deal.price_per_night) {
          toast.error(`La tua offerta (€${formData.offered_price}) deve essere inferiore al prezzo del deal (€${deal.price_per_night})`);
          setLoading(false);
          return;
        }

        // Create counter-offer
        const response = await axios.post(
          `${API}/deal-offers/create`,
          {
            deal_id: deal.id,
            offered_price: parseFloat(formData.offered_price),
            guests: parseInt(formData.guests),
            check_in_date: formData.check_in,
            check_out_date: formData.check_out,
            notes: formData.notes || null
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        toast.success(' Offerta inviata! Il proprietario valuterà la tua proposta.');
        onSuccess && onSuccess(response.data);
        onClose();
      } else {
        // Book at full price (placeholder - you can implement actual booking)
        toast.success('🎉 Prenotazione confermata al prezzo pieno!');
        onSuccess && onSuccess({ deal_id: deal.id, price: deal.price_per_night });
        onClose();
      }
    } catch (error) {
      console.error('Error:', error);
      const errorMsg = error.response?.data?.detail || 'Errore durante l\'operazione';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (!open || !deal) return null;

  const savings = mode === 'counter' 
    ? (deal.price_per_night - formData.offered_price) * calculateNights()
    : 0;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white rounded-none sm:rounded-2xl shadow-2xl w-full sm:max-w-3xl h-full sm:h-auto sm:max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <h2 className="text-lg sm:text-2xl font-bold text-slate-900">Prenota o Fai un'Offerta</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-slate-600" />
          </button>
        </div>

        <div className="p-4 sm:p-6">
          {/* Deal Summary */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 mb-6">
            <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-blue-600" />
              {deal.property_name}
            </h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-slate-600">Città:</span>
                <span className="ml-2 font-medium text-slate-900">{deal.city}</span>
              </div>
              <div>
                <span className="text-slate-600">Prezzo originale:</span>
                <span className="ml-2 font-bold text-slate-900">€{deal.price_per_night}/notte</span>
              </div>
            </div>
            {deal.description && (
              <p className="text-xs text-slate-600 mt-2">{deal.description}</p>
            )}
          </div>

          {/* Mode Selection */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <button
              onClick={() => setMode('full')}
              className={`p-4 rounded-xl border-2 transition-all ${
                mode === 'full'
                  ? 'border-blue-600 bg-blue-50'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              <div className="text-center">
                <div className="text-2xl font-black text-blue-600">€{deal.price_per_night}</div>
                <div className="text-xs text-slate-600 mt-1">Prenota al Prezzo Pieno</div>
              </div>
            </button>

            <button
              onClick={() => setMode('counter')}
              className={`p-4 rounded-xl border-2 transition-all ${
                mode === 'counter'
                  ? 'border-orange-600 bg-orange-50'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              <div className="text-center">
                <TrendingDown className="w-8 h-8 text-orange-600 mx-auto mb-1" />
                <div className="text-xs text-slate-600">Fai un'Offerta al Ribasso</div>
              </div>
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Check-in *
                </label>
                <Input
                  required
                  type="date"
                  value={formData.check_in}
                  onChange={(e) => setFormData({ ...formData, check_in: e.target.value })}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Check-out *
                </label>
                <Input
                  required
                  type="date"
                  value={formData.check_out}
                  onChange={(e) => setFormData({ ...formData, check_out: e.target.value })}
                  min={formData.check_in || new Date().toISOString().split('T')[0]}
                  className="w-full"
                />
              </div>
            </div>

            {/* Guests */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Numero Ospiti *
              </label>
              <Input
                required
                type="number"
                min="1"
                max="20"
                value={formData.guests}
                onChange={(e) => setFormData({ ...formData, guests: e.target.value })}
                className="w-full"
              />
            </div>

            {/* Counter Offer Price */}
            {mode === 'counter' && (
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Quanto sei disposto a pagare? (per notte) *
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-medium">€</span>
                  <Input
                    required
                    type="number"
                    step="0.01"
                    max={deal.price_per_night - 1}
                    value={formData.offered_price}
                    onChange={(e) => setFormData({ ...formData, offered_price: e.target.value })}
                    className="w-full pl-8"
                  />
                </div>
                <p className="text-xs text-slate-600 mt-2">
                  Devi offrire meno di €{deal.price_per_night}/notte
                </p>
              </div>
            )}

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Note / Richieste Speciali
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="es. Arrivo tardivo, necessità parcheggio..."
                className="w-full min-h-[80px] px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Pricing Summary */}
            {calculateNights() > 0 && (
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                <h4 className="font-semibold text-slate-900 mb-2">Riepilogo</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Notti:</span>
                    <span className="font-medium">{calculateNights()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Prezzo per notte:</span>
                    <span className="font-medium">
                      €{mode === 'counter' ? formData.offered_price : deal.price_per_night}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Ospiti:</span>
                    <span className="font-medium">{formData.guests}</span>
                  </div>
                  <div className="border-t border-slate-300 pt-2 mt-2 flex justify-between">
                    <span className="font-bold text-slate-900">Totale:</span>
                    <span className="font-bold text-slate-900">€{calculateTotal().toFixed(2)}</span>
                  </div>
                  {mode === 'counter' && savings > 0 && (
                    <div className="text-emerald-600 text-xs flex items-center gap-1">
                      <TrendingDown className="w-3 h-3" />
                      Risparmio di €{savings.toFixed(2)} rispetto al prezzo pieno
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="flex-1"
                disabled={loading}
              >
                Annulla
              </Button>
              <Button
                type="submit"
                className={`flex-1 ${
                  mode === 'counter'
                    ? 'bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800'
                    : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800'
                }`}
                disabled={loading}
              >
                {loading ? 'Invio...' : mode === 'counter' ? 'Invia Offerta' : 'Prenota Ora'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BookingModalNew;
